import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Input } from '../../../../components/inputs/input/input';
import { Button } from '../../../../components/inputs/button/button';
import FontBody1 from '../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { SingleValue } from 'react-select';
import { ActionResultModal } from '../../../../components/modal/components/action-result-modal/action-result-modal';
import useValidationGroup from '../../../../hooks/validation-group/validation-group-hook';
import GlobalEInstallStore from './global-e-install-store';
import { validateEmail } from '../../../../services/helpers';
import './global-e-credentials-form.scss';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';

interface IGlobalECredentialsFormProps {
    onSuccess?: () => void;
}

export enum GlobalEEnvironment {
    QA = 'qa',
    INT = 'int',
    PROD = 'prod',
}

const ENVIRONMENT_OPTIONS: IOption[] = [
    { value: GlobalEEnvironment.QA, label: 'QA' },
    { value: GlobalEEnvironment.INT, label: 'INT (Integration)' },
    { value: GlobalEEnvironment.PROD, label: 'PROD (Production)' },
];

const GlobalECredentialsForm: React.FC<IGlobalECredentialsFormProps> = observer(({ onSuccess }) => {
    const [store] = useState(() => new GlobalEInstallStore());
    const vGroup = useValidationGroup();

    const handleSubmit = async () => {
        vGroup.validate();
        if (!vGroup.isValid()) {
            return;
        }

        await store.createStore();
        
        if (onSuccess) {
            onSuccess();
        }
    };

    return (
        <div className="global-e-credentials-form">
            <div className="global-e-credentials-form__section">
                <h5 className="global-e-credentials-form__section-title">Store Information</h5>
                
                <div className="troubleshooting-controls">
                    <p className="head5">GE Merchant GUID <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.geMerchantGuid}
                        onChange={store.setGeMerchantGuid}
                        placeholder="GE Merchant GUID"
                        validationGroup={vGroup}
                    />
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">Global-E User ID <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.globalEUserId}
                        onChange={store.setGlobalEUserId}
                        placeholder="Global-E User ID"
                        validationGroup={vGroup}
                    />
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">Shop URL <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.shopUrl}
                        onChange={store.setShopUrl}
                        placeholder="Shop URL (e.g. example.com), without https://"
                        validationGroup={vGroup}
                    />
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">Global-E Environment <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <SelectDropdown
                        required
                        value={ENVIRONMENT_OPTIONS.find(opt => opt.value === store.formData.environment)}
                        options={ENVIRONMENT_OPTIONS}
                        onChange={(val: SingleValue<IOption>) => {
                            if (val) {
                                store.setEnvironment(val.value as GlobalEEnvironment);
                            }
                        }}
                        validationGroup={vGroup}
                    />
                    <p className="global-e-credentials-form__hint">
                        {store.formData.environment === GlobalEEnvironment.QA && 'Base API URL: https://connect-qa.bglobale.com/'}
                        {store.formData.environment === GlobalEEnvironment.INT && 'Base API URL: https://connect.bglobale.com/'}
                        {store.formData.environment === GlobalEEnvironment.PROD && 'Base API URL: https://api.global-e.com/'}
                    </p>
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">Currency <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.currency}
                        onChange={store.setCurrency}
                        placeholder="USD"
                        validationGroup={vGroup}
                    />
                </div>
            </div>

            <div className="global-e-credentials-form__section">
                <h5 className="global-e-credentials-form__section-title">Owner Information</h5>
                
                <div className="troubleshooting-controls">
                    <p className="head5">Email <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        type="email"
                        value={store.formData.email}
                        onChange={store.setEmail}
                        placeholder="owner@example.com"
                        validationGroup={vGroup}
                    />
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">First Name <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.firstname}
                        onChange={store.setFirstname}
                        placeholder="First Name"
                        validationGroup={vGroup}
                    />
                </div>

                <div className="troubleshooting-controls">
                    <p className="head5">Surname <span style={{ color: 'var(--color-accent-3)' }}>*</span></p>
                    <Input
                        required
                        value={store.formData.surname}
                        onChange={store.setSurname}
                        placeholder="Surname"
                        validationGroup={vGroup}
                    />
                </div>
            </div>

            <div className="troubleshooting-buttons-container">
                <Button
                    text="Create Store"
                    onClick={handleSubmit}
                    disabled={store.isLoading}
                    isLoading={store.isLoading}
                    hasLoader
                />
            </div>

            {store.showSuccessModal && store.successData && (
                <ActionResultModal
                    title="Store Installed Successfully"
                    content={
                        <div>
                            <FontBody1 color={FONT_COLOR.PRIMARY3}>
                                {(store.successData.messages ?? []).map((msg, index) => (
                                    <div key={index}>{msg}</div>
                                ))}
                            </FontBody1>
                            <div style={{ marginTop: '16px' }}>
                                <FontBody1 color={FONT_COLOR.PRIMARY3} variant={FONT_VARIANT.BOLD}>
                                    Store ID: {store.successData.shopId}
                                </FontBody1>
                            </div>
                            <div style={{ marginTop: '8px' }}>
                                <FontBody1 color={FONT_COLOR.PRIMARY3} variant={FONT_VARIANT.BOLD}>
                                    Shop URL: {store.successData.shopUrl}
                                </FontBody1>
                            </div>
                        </div>
                    }
                    buttons={[
                        {
                            variant: BUTTON_VARIANTS.PrimaryAccent,
                            text: 'OK',
                            onClick: () => store.setShowSuccessModal(false),
                        },
                    ]}
                    onClose={() => store.setShowSuccessModal(false)}
                    success={true}
                />
            )}
        </div>
    );
});

export default GlobalECredentialsForm;

